import { jsx } from "react/jsx-runtime";
import { useId, useContext, createContext, useMemo, useEffect, useState } from "react";
import { useBlockEnvironment } from "../BlockList/BlockEnvironment/BlockEnvironmentProvider.esm.js";
import { getBlockAnimation, getBlockHover, staggerSlot } from "./animationConfig.esm.js";
const StaggerContext = createContext(null);
const OBSERVER_OPTIONS = {
  rootMargin: "0px 0px -10% 0px",
  threshold: 0
};
const EXIT_OBSERVER_OPTIONS = {
  threshold: 0
};
const canAnimate = () => {
  var _a;
  return typeof window !== "undefined" && typeof window.IntersectionObserver !== "undefined" && !((_a = window.matchMedia) == null ? void 0 : _a.call(window, "(prefers-reduced-motion: reduce)").matches);
};
const isInViewport = (el) => {
  const rect = el.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0;
};
const hide = (el, {
  effect,
  duration,
  delay,
  stagger
}) => {
  el.dataset.cmsAnim = effect;
  el.dataset.cmsAnimDuration = duration;
  el.dataset.cmsAnimDelay = delay;
  if (stagger) {
    el.dataset.cmsAnimStagger = stagger.step;
    el.style.setProperty("--cms-anim-index", String(stagger.index));
  }
  el.dataset.cmsAnimState = "out";
};
const ANIM_ID_ATTR = "data-cms-anim-id";
const findBlockRoot = (uid) => typeof document === "undefined" ? null : document.querySelector(`[${ANIM_ID_ATTR}="${uid}"]`);
const clear = (el) => {
  delete el.dataset.cmsAnim;
  delete el.dataset.cmsAnimState;
  delete el.dataset.cmsAnimDuration;
  delete el.dataset.cmsAnimDelay;
  delete el.dataset.cmsAnimStagger;
  el.style.removeProperty("--cms-anim-index");
};
const firstSeconds = (value) => parseFloat(value) || 0;
const reveal = (el) => {
  el.dataset.cmsAnimState = "in";
  const style = window.getComputedStyle(el);
  const total = firstSeconds(style.transitionDuration) + firstSeconds(style.transitionDelay);
  const timer = window.setTimeout(() => done(), total * 1e3 + 150);
  const onEnd = (event) => {
    if (event.target === el && event.propertyName === "opacity") done();
  };
  function done() {
    window.clearTimeout(timer);
    el.removeEventListener("transitionend", onEnd);
    clear(el);
  }
  el.addEventListener("transitionend", onEnd);
  return () => {
    window.clearTimeout(timer);
    el.removeEventListener("transitionend", onEnd);
  };
};
const toMs = (value, fallback) => {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return fallback;
  return value.trim().endsWith("ms") ? n : n * 1e3;
};
const useHoverScale = (uid, hover) => {
  useEffect(() => {
    var _a;
    if (!hover) return void 0;
    const el = findBlockRoot(uid);
    if (!el || typeof el.animate !== "function") return void 0;
    const reducedMotion = (_a = window.matchMedia) == null ? void 0 : _a.call(window, "(prefers-reduced-motion: reduce)");
    let animation = null;
    const onEnter = () => {
      if (reducedMotion == null ? void 0 : reducedMotion.matches) return;
      if (!animation) {
        const style = window.getComputedStyle(el);
        animation = el.animate([{
          scale: "1"
        }, {
          scale: style.getPropertyValue("--cms-hover-scale").trim() || "1.05"
        }], {
          duration: toMs(style.getPropertyValue("--cms-hover-duration"), 200),
          easing: style.getPropertyValue("--cms-hover-easing").trim() || "ease-out",
          fill: "both"
        });
        return;
      }
      if (animation.playbackRate < 0) animation.reverse();
    };
    const onLeave = () => {
      if (animation && animation.playbackRate > 0) animation.reverse();
    };
    el.addEventListener("pointerenter", onEnter);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointerenter", onEnter);
      el.removeEventListener("pointerleave", onLeave);
      animation == null ? void 0 : animation.cancel();
    };
  }, [uid, hover]);
};
const BlockAnimationRoot = ({
  uid,
  config,
  parent,
  index,
  children
}) => {
  const [phase, setPhase] = useState("pending");
  const mode = config ? config.stagger ? "container" : "self" : "child";
  useEffect(() => {
    if (!config) return void 0;
    const target = findBlockRoot(uid);
    if (!target || !canAnimate()) {
      setPhase("skip");
      return void 0;
    }
    const startsVisible = isInViewport(target);
    if (startsVisible && !config.replay) {
      setPhase("skip");
      return void 0;
    }
    let hidden = false;
    let cancelReveal;
    const hideNow = () => {
      hidden = true;
      cancelReveal == null ? void 0 : cancelReveal();
      cancelReveal = void 0;
      if (mode === "self") {
        hide(target, {
          effect: config.effect,
          duration: config.duration,
          delay: config.delay
        });
      }
      setPhase("out");
    };
    if (startsVisible) {
      setPhase("skip");
    } else {
      hideNow();
    }
    const enter = new IntersectionObserver(([entry]) => {
      if (!(entry == null ? void 0 : entry.isIntersecting) || !hidden) return;
      hidden = false;
      if (!config.replay) enter.disconnect();
      if (mode === "self") cancelReveal = reveal(target);
      setPhase("in");
    }, OBSERVER_OPTIONS);
    enter.observe(target);
    const exit = config.replay ? new IntersectionObserver(([entry]) => {
      if (entry && !entry.isIntersecting && !hidden) hideNow();
    }, EXIT_OBSERVER_OPTIONS) : null;
    exit == null ? void 0 : exit.observe(target);
    return () => {
      enter.disconnect();
      exit == null ? void 0 : exit.disconnect();
      cancelReveal == null ? void 0 : cancelReveal();
      if (mode === "self") clear(target);
    };
  }, [uid, config, mode]);
  const parentPhase = mode === "child" ? parent == null ? void 0 : parent.phase : void 0;
  useEffect(() => {
    if (!parent || parentPhase !== "out" && parentPhase !== "in") {
      return void 0;
    }
    const target = findBlockRoot(uid);
    if (!target) return void 0;
    if (parentPhase === "out") {
      hide(target, {
        effect: parent.effect,
        duration: parent.duration,
        delay: parent.delay,
        stagger: {
          step: parent.stagger.step,
          index: staggerSlot(index, parent.stagger)
        }
      });
      return void 0;
    }
    const cancelReveal = reveal(target);
    return () => {
      cancelReveal();
      clear(target);
    };
  }, [uid, parentPhase, index]);
  const staggerValue = useMemo(() => mode === "container" && (config == null ? void 0 : config.stagger) ? {
    ...config,
    stagger: config.stagger,
    phase
  } : null, [mode, config, phase]);
  return /* @__PURE__ */ jsx(StaggerContext.Provider, { value: staggerValue, children });
};
const AnimatedBlock = ({
  block,
  index,
  renderBlock
}) => {
  var _a;
  const uid = useId();
  const {
    environment
  } = useBlockEnvironment();
  const parent = useContext(StaggerContext);
  const config = useMemo(
    () => getBlockAnimation(block),
    // attrs identity is stable per block payload
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [block.blockName, block.attrs, (_a = block.innerBlocks) == null ? void 0 : _a.length]
  );
  const inEditor = environment === "editor";
  const animated = !inEditor && Boolean(config || parent);
  const hover = inEditor ? null : getBlockHover(block);
  const domProps = useMemo(() => ({
    ...animated || hover ? {
      [ANIM_ID_ATTR]: uid
    } : {},
    ...hover ? {
      "data-cms-hover": hover
    } : {}
  }), [animated, hover, uid]);
  const element = useMemo(
    () => renderBlock(domProps),
    // renderBlock is a fresh closure per parent render; block + domProps
    // fully determine its output.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [block, domProps]
  );
  useHoverScale(uid, hover);
  if (!animated) return element;
  return /* @__PURE__ */ jsx(BlockAnimationRoot, { uid, config, parent, index, children: element });
};
export {
  AnimatedBlock
};
